import {
  Alert,
  App as AntdApp,
  Button,
  DatePicker,
  Form,
  Modal,
  Select,
  Upload,
  message,
} from "antd";
import { InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useApiUrl, useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { tokenService } from "../../../services/storage/tokenService";
import { useAssignableAuditors } from "./useAssignableAuditors";

interface RouteEditModalProps {
  visible: boolean;
  onClose: () => void;
  route: {
    id: number;
    route_date?: string | null;
    auditor?: number | null;
    auditor_name?: string | null;
    sources_count?: number;
    has_geometry?: boolean;
  } | null;
  auditors: Array<{ id: number; name: string }>;
  auditCompanyId?: number | null;
  countryId?: number | null;
  isAuditorOnly?: boolean;
  currentUser?: { id: number; name: string } | null;
  onSuccess?: () => void;
}

interface RouteEditFormValues {
  route_date: dayjs.Dayjs | null;
  auditor: number | null;
  gpx_file?: UploadFile[];
}

const ALLOWED_EXTS = [".gpx", ".kml"];
const ACCEPT_ATTR = ALLOWED_EXTS.join(",");
const hasAllowedExt = (name: string) =>
  ALLOWED_EXTS.some((ext) => name.toLowerCase().endsWith(ext));

/**
 * RouteEditModal — change `route_date` and `auditor` of an existing
 * RoutePerformed entry. The GPX file is left untouched (use upload modal
 * to replace it; the dedicated endpoint for that is out of scope here).
 */
export const RouteEditModal = ({
  visible,
  onClose,
  route,
  auditors,
  auditCompanyId,
  countryId,
  isAuditorOnly = false,
  currentUser,
  onSuccess,
}: RouteEditModalProps) => {
  const { t } = useTranslation();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm<RouteEditFormValues>();
  const { mutate: updateRoute } = useUpdate();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { message: notify } = AntdApp.useApp();

  const auditorPicker = useAssignableAuditors({
    visible,
    auditCompanyId,
    countryId,
    isAuditorOnly,
    currentUser,
    fallback: auditors,
  });

  useEffect(() => {
    if (visible && route) {
      form.setFieldsValue({
        route_date: route.route_date ? dayjs(route.route_date) : null,
        // When locked to the current user, force them as the value even
        // if the route was originally assigned to someone else — they
        // can only re-attribute it to themselves.
        auditor: auditorPicker.locked
          ? auditorPicker.defaultValue ?? null
          : route.auditor ?? null,
      });
      setFileList([]);
    } else if (!visible) {
      form.resetFields();
      setFileList([]);
    }
  }, [
    visible,
    route,
    form,
    auditorPicker.locked,
    auditorPicker.defaultValue,
  ]);

  const beforeUpload = (file: File) => {
    if (!hasAllowedExt(file.name)) {
      notify.error(
        t("route.invalid_route_file", "El archivo debe ser GPX (.gpx) o KML (.kml)"),
      );
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleFileChange = (info: any) => {
    setFileList(info.fileList.slice(0, 10));
  };

  const uploadAdditionalFiles = async (): Promise<boolean> => {
    if (!route || fileList.length === 0) return true;
    const originals = fileList
      .map((f) => f.originFileObj as File | undefined)
      .filter((f): f is File => f != null);
    if (originals.length === 0) return true;

    const formData = new FormData();
    for (const f of originals) {
      formData.append("files", f, f.name);
    }
    const accessToken = tokenService.getAccessToken();
    try {
      // El backend acepta PATCH multipart y hace append sobre la ruta
      // existente (añade los ficheros al ZIP, extiende la geometría y
      // registra nuevos sources). No forzamos POST porque esta modal
      // edita una fila concreta por id.
      await axios.patch(`${apiUrl}/routes/${route.id}/`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return true;
    } catch (err: any) {
      const detail =
        err?.response?.data?.files?.[0]
        || err?.response?.data?.detail
        || err?.message
        || t("route.upload_error", "Error al añadir archivos a la ruta");
      notify.error(detail);
      return false;
    }
  };

  const handleSubmit = async (values: RouteEditFormValues) => {
    if (!route) return;
    if (!values.route_date) {
      message.error(t("route.route_date_required", "La fecha de ruta es obligatoria"));
      return;
    }
    if (!values.auditor) {
      message.error(t("route.auditor_required", "Selecciona un auditor"));
      return;
    }
    setSubmitting(true);

    // Paso 1 — subida de nuevos archivos. Si falla, no seguimos con
    // el PATCH de metadatos (evita quedarnos con cambios parciales
    // tipo "sí renombré la fecha pero el GPX nuevo no entró").
    const filesOk = await uploadAdditionalFiles();
    if (!filesOk) {
      setSubmitting(false);
      return;
    }

    // Paso 2 — PATCH de los metadatos (route_date, auditor). Lo
    // separamos del upload porque Refine's useUpdate manda JSON y el
    // append de ficheros exige multipart; mezclarlos obligaría a
    // transformar el payload entero.
    updateRoute(
      {
        resource: "routes",
        id: route.id,
        values: {
          route_date: dayjs(values.route_date).format("YYYY-MM-DD"),
          auditor: values.auditor,
        },
      },
      {
        onSuccess: () => {
          setSubmitting(false);
          notify.success(
            fileList.length > 0
              ? t(
                  "route.update_and_append_success",
                  "Ruta actualizada y archivos añadidos",
                )
              : t("route.update_success", "Ruta actualizada"),
          );
          setFileList([]);
          onClose();
          onSuccess?.();
        },
        onError: (error: any) => {
          setSubmitting(false);
          const detail =
            error?.originalError?.response?.data?.detail
            || error?.response?.data?.detail
            || error?.message;
          message.error(detail || t("route.update_error", "No se pudo actualizar la ruta"));
        },
      },
    );
  };

  return (
    <Modal
      title={t("route.edit", "Editar ruta")}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save", "Guardar")}
      cancelText={t("common.cancel", "Cancelar")}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item
          name="route_date"
          label={t("route.route_date", "Fecha de ruta")}
          rules={[
            {
              required: true,
              message: `${t("route.route_date", "Fecha de ruta")} ${t("common.required", "es obligatorio")}`,
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={t("route.route_date_placeholder", "Selecciona la fecha")}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
        </Form.Item>

        <Form.Item
          name="auditor"
          label={t("route.auditor", "Auditor")}
          rules={[
            {
              required: true,
              message: `${t("route.auditor", "Auditor")} ${t("common.required", "es obligatorio")}`,
            },
          ]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t("common.select", "Selecciona")}
            loading={auditorPicker.loading}
            disabled={auditorPicker.locked}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            options={auditorPicker.options}
          />
        </Form.Item>

        {(() => {
          const sc = route?.sources_count ?? 0;
          const hasGeom = route?.has_geometry !== false; // undefined counts as "yes"
          let msg: string;
          if (sc > 0) {
            // Caso normal post-refactor: hay sources registradas.
            msg = t(
              "route.append_from_edit_notice",
              "Esta ruta ya tiene {{count}} archivo(s). Los nuevos ficheros se AÑADIRÁN al recorrido existente.",
              { count: sc },
            );
          } else if (hasGeom) {
            // Legacy: la ruta tiene geometría de un GPX subido antes
            // del refactor multi-upload (sources_count=0 pero el ZIP
            // sí contiene el fichero original). Al añadir ficheros,
            // el backend lo preserva como primer source implícito y
            // mergea los nuevos encima.
            msg = t(
              "route.append_legacy_notice",
              "Esta ruta ya tiene geometría (de un GPX subido antes del refactor). Los nuevos ficheros se AÑADIRÁN al recorrido existente.",
            );
          } else {
            msg = t(
              "route.append_empty_notice",
              "La ruta está vacía. Puedes subir uno o varios GPX/KML para generar la geometría.",
            );
          }
          return (
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 12 }}
              message={msg}
              description={
                hasGeom || sc > 0 ? (
                  <span style={{ fontSize: 12 }}>
                    {t(
                      "route.append_hint",
                      "Para empezar de cero, abre el recorrido en 'Ver ruta' y usa 'Resetear'.",
                    )}
                  </span>
                ) : null
              }
            />
          );
        })()}

        <Form.Item
          name="gpx_file"
          label={t(
            "route.route_files_add",
            "Añadir más archivos (GPX o KML, opcional)",
          )}
        >
          <Upload
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            multiple
            maxCount={10}
            accept={ACCEPT_ATTR}
          >
            <Button icon={<UploadOutlined />}>
              {t(
                "route.select_route_files",
                "Seleccionar archivos (.gpx / .kml)",
              )}
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
